// src/pages/Resetpassword.jsx
import React, { useMemo, useState } from "react";
import Meta from "../components/Meta";
import Container from "../components/Container";
import Custominput from "../components/Custominput";
import {
  Link,
  useNavigate,
  useLocation,
  useSearchParams,
  useParams,
} from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { FiEye, FiEyeOff } from "react-icons/fi";

import { useDispatch, useSelector } from "react-redux";
import { resetPasswordTok } from "../app/features/user/userSlice";

import kuptoLogo from "../images/logo/kupto logobb-8.png";
import "./styles/resetpassword.css";

const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const passwordRules = (pw) => {
  const s = String(pw || "");
  return {
    len: s.length >= 8,
    upper: /[A-Z]/.test(s),
    lower: /[a-z]/.test(s),
    num: /\d/.test(s),
  };
};

const isStrongPassword = (pw) => {
  const r = passwordRules(pw);
  return r.len && r.upper && r.lower && r.num;
};

const Resetpassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [sp] = useSearchParams();
  const params = useParams();

  const authLoading = useSelector((state) => state?.auth?.isLoading);
  const isLoading = Boolean(authLoading);

  // ✅ backend expects EMAIL + CODE + PASSWORD
  const codeFromParam = params?.token || params?.code || "";
  const codeFromQuery = sp.get("code") || sp.get("token") || "";
  const codeFromState = location?.state?.code || location?.state?.token || "";

  const code = useMemo(
    () => String(codeFromParam || codeFromQuery || codeFromState || "").trim(),
    [codeFromParam, codeFromQuery, codeFromState]
  );

  const emailFromState = location?.state?.email || location?.state?.identity || "";
  const emailFromQuery = sp.get("email") || sp.get("identity") || "";

  const email = useMemo(
    () => String(emailFromState || emailFromQuery || "").trim().toLowerCase(),
    [emailFromState, emailFromQuery]
  );

  const missingEmail = !email || !isValidEmail(email);
  const missingCode = !code || String(code).length < 4;

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [touched, setTouched] = useState(false);
  const [apiErr, setApiErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [showPw2, setShowPw2] = useState(false);

  const rules = useMemo(() => passwordRules(password), [password]);
  const pwOk = useMemo(() => isStrongPassword(password), [password]);

  const matchOk = useMemo(
    () => String(password || "") && String(confirm || "") && password === confirm,
    [password, confirm]
  );

  const canSubmit = !missingEmail && !missingCode && pwOk && matchOk && !isLoading;

  const showErrPw = touched && !!password && !pwOk;
  const showErrMatch = touched && !!confirm && password !== confirm;

  const goRequestNewCode = () => {
    navigate("/forgot-password", {
      replace: true,
      state: { identity: email, type: "email" },
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setTouched(true);
    setApiErr("");
    setOkMsg("");

    if (missingEmail) {
      setApiErr("Email is missing. Please go back and request a new reset code.");
      return;
    }
    if (missingCode) {
      setApiErr("Reset code is missing. Please verify your reset code again.");
      return;
    }
    if (!pwOk) {
      setApiErr("Please choose a stronger password.");
      return;
    }
    if (!matchOk) {
      setApiErr("Passwords do not match.");
      return;
    }

    try {
      await dispatch(resetPasswordTok({ email, code, password })).unwrap();

      setOkMsg("Password reset successful. Redirecting to sign in...");
      setPassword("");
      setConfirm("");

      setTimeout(() => {
        navigate("/login", {
          replace: true,
          state: { identity: email, type: "email" },
        });
      }, 600);
    } catch (err) {
      console.error(err);
      const msg =
        typeof err === "string"
          ? err
          : err?.message || "Failed to reset password. Please try again.";
      setApiErr(msg);
    }
  };

  const showExpiredAction =
    String(apiErr || "").toLowerCase().includes("expired") ||
    String(apiErr || "").toLowerCase().includes("request a new code");

  return (
    <>
      <Meta title="Reset password" />

      {/* ✅ FULL-WIDTH TOP LOADER (fixed, like Identify/Login/ForgotPassword) */}
      {isLoading ? (
        <div className="ku-rp-topLoader" role="status" aria-label="Loading">
          <span className="ku-rp-topTrack" aria-hidden="true">
            <span className="ku-rp-topBar" />
          </span>
        </div>
      ) : null}

      <Container class1="ku-rp-wrap">
        <div className="ku-rp-shell">
          <div className={`ku-rp-split ${isLoading ? "is-busy" : ""}`}>
            <button
              type="button"
              className="ku-rp-back ku-rp-back--appOnly"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              disabled={isLoading}
            >
              <IoArrowBack />
            </button>

            {/* LEFT */}
            <div className="ku-rp-left">
              <div className="ku-rp-brand" aria-hidden="true">
                <div className="ku-rp-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-rp-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-rp-head">
                <h1 className="ku-rp-title">Reset password</h1>

                {missingEmail || missingCode ? (
                  <p className="ku-rp-sub">
                    {missingEmail
                      ? "Email is missing. Please go back and request a reset again."
                      : "Reset code is missing. Please verify your reset code again."}
                  </p>
                ) : (
                  <p className="ku-rp-sub">Choose a new password.</p>
                )}
              </div>

              <form className="ku-rp-form" onSubmit={onSubmit} noValidate>
                {/* Password */}
                <div className="ku-rp-field">
                  <div
                    className={`ku-rp-input ku-rp-input--pw ${
                      showErrPw ? "is-error" : ""
                    }`}
                  >
                    <div className="ku-rp-pwBox">
                      <Custominput
                        type={showPw ? "text" : "password"}
                        name="password"
                        placeholder="New password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onBlur={() => setTouched(true)}
                        disabled={isLoading || missingEmail || missingCode}
                      />

                      <button
                        type="button"
                        className="ku-rp-eyeBtn"
                        onClick={() => setShowPw((s) => !s)}
                        aria-label={showPw ? "Hide password" : "Show password"}
                        disabled={isLoading || missingEmail || missingCode}
                        tabIndex={0}
                      >
                        {showPw ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>

                  <div className="ku-rp-rules">
                    <span className={`ku-rp-rule ${rules.len ? "ok" : ""}`}>
                      8+ chars
                    </span>
                    <span className={`ku-rp-rule ${rules.upper ? "ok" : ""}`}>
                      Uppercase
                    </span>
                    <span className={`ku-rp-rule ${rules.lower ? "ok" : ""}`}>
                      Lowercase
                    </span>
                    <span className={`ku-rp-rule ${rules.num ? "ok" : ""}`}>
                      Number
                    </span>
                  </div>
                </div>

                {/* Confirm */}
                <div className="ku-rp-field">
                  <div
                    className={`ku-rp-input ku-rp-input--pw ${
                      showErrMatch ? "is-error" : ""
                    }`}
                  >
                    <div className="ku-rp-pwBox">
                      <Custominput
                        type={showPw2 ? "text" : "password"}
                        name="confirm"
                        placeholder="Confirm password"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        onBlur={() => setTouched(true)}
                        disabled={isLoading || missingEmail || missingCode}
                      />

                      <button
                        type="button"
                        className="ku-rp-eyeBtn"
                        onClick={() => setShowPw2((s) => !s)}
                        aria-label={showPw2 ? "Hide password" : "Show password"}
                        disabled={isLoading || missingEmail || missingCode}
                        tabIndex={0}
                      >
                        {showPw2 ? <FiEyeOff /> : <FiEye />}
                      </button>
                    </div>
                  </div>
                </div>

                {apiErr ? <div className="ku-rp-error">{apiErr}</div> : null}

                {showExpiredAction ? (
                  <button
                    type="button"
                    className="ku-rp-ghostBtn"
                    onClick={goRequestNewCode}
                    disabled={isLoading}
                  >
                    Request new code
                  </button>
                ) : null}

                {okMsg ? <div className="ku-rp-info">{okMsg}</div> : null}

                <button type="submit" className="ku-rp-btn" disabled={!canSubmit}>
                  Reset Password
                </button>

                <div className="ku-rp-actionsRow">
                  <Link className="ku-rp-linkBtn" to="/login">
                    Back to Sign in
                  </Link>
                </div>

                <p className="ku-rp-legal">
                  By continuing you agree to Kupto’s{" "}
                  <Link to="/terms-and-conditions">Terms and Conditions</Link>
                </p>
              </form>

              <span className="ku-rp-glow ku-rp-glow--1" />
              <span className="ku-rp-glow ku-rp-glow--2" />
            </div>

            {/* RIGHT (desktop only). Hidden on app via CSS */}
            <div className="ku-rp-right ku-rp-right--desktopOnly">
              <h3 className="ku-rp-rightTitle">Tips</h3>

              <div className="ku-rp-tipList">
                <div className="ku-rp-tip">
                  <div className="ku-rp-tipIcon">🔐</div>
                  <div className="ku-rp-tipTxt">
                    <p className="ku-rp-tipH">Use a strong password</p>
                    <p className="ku-rp-tipP">
                      Combine uppercase, lowercase, numbers, and avoid common words.
                    </p>
                  </div>
                </div>

                <div className="ku-rp-tip">
                  <div className="ku-rp-tipIcon">⏳</div>
                  <div className="ku-rp-tipTxt">
                    <p className="ku-rp-tipH">Reset codes expire</p>
                    <p className="ku-rp-tipP">
                      If it fails, verify your reset code again and retry.
                    </p>
                  </div>
                </div>
              </div>

              <div className="ku-rp-smallCard">
                <p className="ku-rp-smallTitle">Need help?</p>
                <p className="ku-rp-smallText">
                  If you didn’t request a reset, you can ignore this and sign in normally.
                </p>
                <Link to="/contact" className="ku-rp-smallBtn">
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export default Resetpassword;
